import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, useReducedMotion } from 'motion/react'
import { AlertTriangle } from 'lucide-react'
import { LogoHeader } from '../components/layout'
import { Button } from '../components/ui/Button'

// Landing page for failed magic-link sign-ins. The backend redirects here with
// ?reason= matching apps/api/internal/auth/handlers.go.
const COPY: Record<string, { title: string; body: string }> = {
  expired: {
    title: 'This link has expired',
    body: 'Sign-in links are valid for 15 minutes. Request a new code and we will send you a fresh link.',
  },
  locked: {
    title: 'Account access is locked',
    body: 'Please contact support so they can verify your identity and restore access.',
  },
  invalid: {
    title: 'This link is no longer valid',
    body: 'The link may have already been used or replaced by a newer email. Request a new code to continue.',
  },
  error: {
    title: 'Something went wrong',
    body: 'We could not sign you in with that link. Please try again with a new code.',
  },
}

export function AuthErrorScreen() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const shouldReduceMotion = useReducedMotion()
  const { title, body } = COPY[params.get('reason') ?? 'error'] ?? COPY.error

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-surface-base)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-4)',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: shouldReduceMotion ? 0 : 0.4, ease: [0.0, 0.0, 0.2, 1.0] }}
        style={{
          background: 'var(--color-surface-container-lowest)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-ambient)',
          padding: 'var(--spacing-10)',
          width: '100%',
          maxWidth: '440px',
        }}
      >
        <LogoHeader />

        <div
          aria-hidden="true"
          style={{
            width: 56,
            height: 56,
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-warning-surface)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          <AlertTriangle size={28} color="var(--color-warning-text)" />
        </div>

        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--weight-semibold)',
            fontSize: 'var(--text-title-lg)',
            color: 'var(--color-text-primary)',
            lineHeight: 'var(--leading-tight)',
            margin: '0 0 var(--spacing-3) 0',
          }}
        >
          {title}
        </h1>

        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-md)',
            color: 'var(--color-text-secondary)',
            lineHeight: 'var(--leading-normal)',
            margin: '0 0 var(--spacing-8) 0',
          }}
        >
          {body}
        </p>

        <Button variant="primary" style={{ width: '100%' }} onClick={() => navigate('/login')}>
          Back to sign in
        </Button>
      </motion.div>
    </div>
  )
}
